import { useEffect, useRef, useState, Component, lazy as _reactLazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import SpaLinks from './components/SpaLinks';
import { BASE } from './lib/basePath';

/**
 * 0.38.52 - lazyWithRetry: robust lazy() replacement.
 *
 * React.lazy() promises reject once and never retry. When a chunk
 * import fails - transient network hiccup, a Vite hashed asset that
 * changed between the HTML load and the click that triggers the
 * chunk import (deploy happened mid-session), or a browser that
 * dropped the fetch - Suspense catches the rejection and renders
 * the fallback forever or nothing at all. From the admin's side:
 * blank page on first navigation to a route, refresh works.
 *
 * This wrapper adds:
 *   - Automatic retry with a small backoff on the first failure.
 *   - On persistent failure that looks chunk-hash-related (a
 *     ChunkLoadError, or the message mentions "Loading chunk"),
 *     force a hard page reload so the browser re-fetches
 *     /index.html and picks up the current asset URLs. Guarded
 *     with a sessionStorage flag so we don't reload in a loop.
 *   - Otherwise rethrows so the ErrorBoundary below shows an
 *     explanatory message instead of a silent blank.
 *
 * Same call signature as React.lazy - drop-in.
 */
function lazyWithRetry(importer) {
  return _reactLazy(async () => {
    try {
      return await importer();
    } catch (err) {
      // First failure: retry once after a short delay. Handles
      // transient network hiccups without a full reload.
      try {
        await new Promise(r => setTimeout(r, 400));
        return await importer();
      } catch (err2) {
        const msg = (err2 && err2.message) || String(err2 || '');
        const looksLikeStaleChunk = /Loading chunk|ChunkLoadError|Failed to fetch dynamically imported/i.test(msg);
        if (looksLikeStaleChunk) {
          const KEY = 'gt18_lazy_reload_at';
          const now = Date.now();
          let lastReload = 0;
          try { lastReload = parseInt(sessionStorage.getItem(KEY) || '0', 10) || 0; } catch (_) { /* private mode */ }
          // If we haven't reloaded in the last 30 seconds, do it.
          // Otherwise we're in a loop - let the error boundary show
          // so admin sees the actual problem instead of a boot loop.
          if (now - lastReload > 30_000) {
            try { sessionStorage.setItem(KEY, String(now)); } catch (_) { /* ignore */ }
            // Add a cache-buster so the reload does not reuse the
            // same stale HTML that referenced the missing chunk.
            const u = new URL(window.location.href);
            u.searchParams.set('_r', String(now));
            window.location.replace(u.toString());
            // Return a component that renders nothing while the
            // reload is in flight - Suspense will unmount before
            // it matters.
            return { default: () => null };
          }
        }
        throw err2;
      }
    }
  });
}
const lazy = lazyWithRetry;

/**
 * 0.38.52 - Error boundary for the Suspense-wrapped Routes. Before
 * this, a lazy-load rejection or a render-time throw in any admin
 * page fell through to a blank screen with no console output beyond
 * React's default red banner (which most admins never open DevTools
 * to see). Now the error surfaces as text with a Reload button.
 */
class RouteErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error('[RouteErrorBoundary]', err, info);
  }
  render() {
    if (this.state.err) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2 style={{ marginBottom: 'var(--space-3)' }}>Page failed to load</h2>
          <p style={{ color: 'var(--color-text-600)', marginBottom: 'var(--space-4)' }}>
            {String(this.state.err.message || this.state.err)}
          </p>
          <button
            type="button"
            onClick={() => { this.setState({ err: null }); window.location.reload(); }}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
// 0.36.293 — Platform header / footer config (background colors,
// logo, brand text, footer body, etc.). Provider fetches once on
// mount; <Header> / <Footer> consume.
import { LayoutConfigProvider, useLayoutConfig } from './context/LayoutConfigContext';
// 0.36.227 — Ship B: hydrate the timestamp formatter at mount
import { publicSettingsApi } from './api/admin';
import { setSystemTimeFormat } from './utils/format';
// 0.36.278 — Hydrate the dropdown behavior thresholds (inline + search)
// at mount from the same /api/public/settings endpoint. HenaketDropdown
// and HenaketMultiDropdown read from this module at render time;
// defaults (7 / 15) cover the pre-fetch window so first render is
// unblocked.
import { setDropdownThresholds } from './utils/dropdownThresholds';
// 0.38.16 - Hydrate the platform-wide number-format defaults for
// the expression engine's cascade. Blank defaults mean "no auto
// formatting" so nothing changes for existing forms until admin
// sets a value in Platform Settings.
import { setNumberFormatDefaults } from './utils/numberFormat';
import Header from './components/Header';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import RoleRoute from './components/RoleRoute';
import Landing from './pages/Landing';
import StaticPage from './pages/StaticPage';
// 0.37.62 — Public /p/ URLs now carry a language segment. See
// each component for details.
import LangStaticPageRoute from './components/LangStaticPageRoute';
// 0.42.62 - Flows.
import FlowRunner from './pages/FlowRunner';
import RedirectToLangStaticPage from './components/RedirectToLangStaticPage';
// 0.39.22 - Bare-UUID alias -> canonical page redirect.
import AliasResolver from './components/AliasResolver';
// 0.36.733 — Hostname-aware routing for the dedicated admin
// subdomain (gt18-admin.govtech.am). Same SPA build, branches
// on hostname to expose admin-only routes there.
import { isAdminHost, setAdminPanelUrl, setPublicSiteUrl } from './utils/adminHost';
const AdminStaticPages = lazy(() => import('./pages/admin/AdminStaticPages'));
// 0.36.292 — Platform-wide header / footer config screens. Each
// surfaces as a virtual row in the Pages and Forms table; the
// click target is here.
const AdminLayoutHeader = lazy(() => import('./pages/admin/AdminLayoutHeader'));
const AdminLayoutFooter = lazy(() => import('./pages/admin/AdminLayoutFooter'));
// 0.49.66 - Static: the Login page (LoginPage, on the home page) uses it
// anyway, so a lazy import only produced a build warning.
import Login from './pages/Login';
import LoginPage from './pages/LoginPage';
const SignupPage = lazy(() => import('./pages/auth/SignupPage'));  // 0.49.63 - loaded when opened
const LoginVerifyPage = lazy(() => import('./pages/auth/LoginVerifyPage'));  // 0.49.63 - loaded when opened
const LoginIdCardPage = lazy(() => import('./pages/auth/LoginIdCardPage'));  // 0.49.63 - loaded when opened
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));  // 0.49.63 - loaded when opened
const AcceptInvitePage = lazy(() => import('./pages/auth/AcceptInvitePage'));  // 0.49.63 - loaded when opened
const LoginVerify = lazy(() => import('./pages/LoginVerify'));  // 0.49.63 - loaded when opened
const LoginIdCard = lazy(() => import('./pages/LoginIdCard'));  // 0.49.63 - loaded when opened
const Signup = lazy(() => import('./pages/Signup'));  // 0.49.63 - loaded when opened
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));  // 0.49.63 - loaded when opened
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'));  // 0.49.63 - loaded when opened
const Dashboard = lazy(() => import('./pages/Dashboard'));  // 0.49.63 - loaded when opened
const Profile = lazy(() => import('./pages/Profile'));  // 0.49.63 - loaded when opened
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminOverview = lazy(() => import('./pages/admin/AdminOverview'));
const AdminLanguages = lazy(() => import('./pages/admin/AdminLanguages'));
const AdminTranslations = lazy(() => import('./pages/admin/AdminTranslations'));
const AdminEmailTemplates = lazy(() => import('./pages/admin/AdminEmailTemplates'));
const AdminEmailTemplateEditor = lazy(() => import('./pages/admin/AdminEmailTemplateEditor'));
const AdminSettingsHome = lazy(() => import('./pages/admin/AdminSettingsHome'));
// 0.41.06 - Registered outbound API connections.
const AdminApiConnections = lazy(() => import('./pages/admin/AdminApiConnections'));
// 0.42.77 - Flows: an order over existing pages.
const AdminFlows = lazy(() => import('./pages/admin/AdminFlows'));
// 0.44.50 - Collections (kinds of site content).
const AdminCollections = lazy(() => import('./pages/admin/AdminCollections'));
// 0.49.83 - Data tables.
const AdminDataTables = lazy(() => import('./pages/admin/AdminDataTables'));
const AdminDataTable = lazy(() => import('./pages/admin/AdminDataTable'));
// 0.45.45 - The old Collections address opens Libraries on the Content
// libraries tab.
function CollectionsRedirect() {
  try { sessionStorage.setItem('admin-libraries-tab', 'content'); } catch { /* no storage */ }
  return <Navigate to="/admin/libraries" replace />;
}
const AdminSiteContent = lazy(() => import('./pages/admin/AdminSiteContent'));
const AdminContentItems = lazy(() => import('./pages/admin/AdminContentItems'));
const AdminContentItem = lazy(() => import('./pages/admin/AdminContentItem'));
const AdminLibrariesHome = lazy(() => import('./pages/admin/AdminLibrariesHome'));
// 0.36.285 — Admin CRUD for custom dropdown libraries.
// 0.36.351 — Generic library detail page (read-only viewer with
// language tabs). Currently wired for custom libraries; built-in
// libraries continue to use their dedicated routes.
const AdminLibraryDetail = lazy(() => import('./pages/admin/AdminLibraryDetail'));
// 0.36.354 — Dedicated references page reached from the Usage
// column on the Libraries tab. Lists every form element that uses
// the library.
const AdminLibraryReferences = lazy(() => import('./pages/admin/AdminLibraryReferences'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
// 0.41.101 - ID card login: its own page under Settings.
const AdminIdCard = lazy(() => import('./pages/admin/AdminIdCard'));
const AdminBranding = lazy(() => import('./pages/admin/AdminBranding'));
const AdminSystemIdentity = lazy(() => import('./pages/admin/AdminSystemIdentity'));
const AdminIpAccess = lazy(() => import('./pages/admin/AdminIpAccess'));
const AdminAddresses = lazy(() => import('./pages/admin/AdminAddresses'));
const AdminFormEditor = lazy(() => import('./pages/admin/AdminFormEditor'));
// 0.37.29 — Behavior rule editor as a routed page (not modal /
// inline swap) — see AdminRuleEditor.jsx.
const AdminRuleEditor = lazy(() => import('./pages/admin/AdminRuleEditor'));
const AdminFormPreview = lazy(() => import('./pages/admin/AdminFormPreview'));
const AdminGeoIP = lazy(() => import('./pages/admin/AdminGeoIP'));
// 0.36.111 — Documentation pages inside the admin shell.
// 0.36.112 — Forms guide added; /admin/docs now an index page.
const AdminDocsIndex = lazy(() => import('./pages/admin/AdminDocsIndex'));
const AdminDocsRules = lazy(() => import('./pages/admin/AdminDocsRules'));
const AdminDocsForms = lazy(() => import('./pages/admin/AdminDocsForms'));
// 0.36.384 — Libraries guide (CSV import/export first; creation +
// management sections planned).
const AdminDocsLibraries = lazy(() => import('./pages/admin/AdminDocsLibraries'));
// 0.36.505 — Additional in-app doc pages: dropdown behavior,
// system settings, library management (reference), library
// reference columns. Each mirrors a root-level docs/*.md file.
const AdminDocsDropdowns = lazy(() => import('./pages/admin/AdminDocsDropdowns'));
const AdminDocsSystemSettings = lazy(() => import('./pages/admin/AdminDocsSystemSettings'));
const AdminDocsDataTables = lazy(() => import('./pages/admin/AdminDocsDataTables'));
const AdminLayoutNav = lazy(() => import('./pages/admin/AdminLayoutNav'));
const AdminDocsApiConnections = lazy(() => import('./pages/admin/AdminDocsApiConnections'));
const AdminDocsAiConnectors = lazy(() => import('./pages/admin/AdminDocsAiConnectors'));
const AdminDocsLibraryManagement = lazy(() => import('./pages/admin/AdminDocsLibraryManagement'));
const AdminDocsLibraryReferences = lazy(() => import('./pages/admin/AdminDocsLibraryReferences'));
const AdminTwoFactor = lazy(() => import('./pages/admin/AdminTwoFactor'));
// 0.36.374 — AdminIsoLanguageCodes, AdminIsoCountryCodes,
// AdminIsoOfficialNames, AdminIsoDialCodes imports removed. Those
// pages retired in favor of AdminLibraryDetail's unified inline
// CRUD (ships 0.36.370 → 0.36.373). Old routes now redirect to
// the unified detail page — see the redirect block below.
const AdminSignupMode = lazy(() => import('./pages/admin/AdminSignupMode'));
// 0.36.225 — Time & date settings page
const AdminTimeDate = lazy(() => import('./pages/admin/AdminTimeDate'));
// 0.38.16 - Platform-wide number-format defaults (baseline of the
// three-tier cascade: platform -> per-form -> per-expression).
const AdminNumberFormat = lazy(() => import('./pages/admin/AdminNumberFormat'));
const AdminButtonsFocus = lazy(() => import('./pages/admin/AdminButtonsFocus'));
// 0.36.278 — Dropdown behavior settings (inline expansion + search thresholds).
const AdminDropdownBehavior = lazy(() => import('./pages/admin/AdminDropdownBehavior'));
// 0.44.51 - Settings > Icons.
const AdminIcons = lazy(() => import('./pages/admin/AdminIcons'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminUserDetail = lazy(() => import('./pages/admin/AdminUserDetail'));
const AdminAuditLog = lazy(() => import('./pages/admin/AdminAuditLog'));
// 0.36.345 — AdminDiagnostics page removed. Server resources + System
// log files panels moved to AdminOverview's System status card.
const AdminHelpdesk = lazy(() => import('./pages/admin/AdminHelpdesk'));
import Force2faModal from './components/Force2faModal';
import { setSystemIdentity } from './lib/systemIdentity';
import { applyUiTheme } from './lib/uiTheme';
import AccessGate from './components/AccessGate';

function PublicShell({ children }) {
  return (
    <>
      <Header />
      {children}
      {/* 0.45.07 - The space above the footer (was the footer's own
          margin-top, which is now auto so the footer sits at the
          bottom of a short page). */}
      <div aria-hidden="true" style={{ height: 'var(--space-12)', flexShrink: 0 }} />
      <Footer />
      <Force2faModal />
    </>
  );
}

function AppRouter() {
  const { loading, error, setLanguageLocal, languages } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();
  // 0.36.742 — Read the header layout config so we can suppress the
  // /login family of public routes when admin has hidden the Мутк
  // button. When show_login_button is false, the citizen site is
  // meant to be fully public — the URL routes for login should not
  // work either. Admins log in via gt18-admin.govtech.am regardless.
  const { header: headerConfig } = useLayoutConfig();
  // 0.49.29 - A site that needs signing in keeps its sign-in pages,
  // whatever the header's login button says.
  const [signinMode, setSigninMode] = useState('');
  const loginHiddenOnPublic = headerConfig?.show_login_button === false && !signinMode;
  // /admin/forms/[id]/preview is EXCLUDED from the admin-shell branch
  // even though its URL starts with /admin. The preview's purpose is
  // to simulate the citizen experience — admin sidebar / chrome would
  // defeat that purpose. By falling through to the public branch the
  // preview gets the standard Header + Footer (via PublicShell) and
  // any full-bleed elements in the form (e.g. separator) actually
  // span the viewport as designed. Admin authentication still applies
  // via the AdminRoute + RoleRoute wrappers on the preview route in
  // the public Routes block below.
  const FORM_PREVIEW_RX = /^\/admin\/forms\/[^/]+\/preview$/;
  const isAdminRoute = location.pathname.startsWith('/admin')
                    && !FORM_PREVIEW_RX.test(location.pathname);

  // When a user signs in (or comes back to a stored session), sync the UI
  // language to their server-side preferred_language ONCE per change in that
  // server-side value. We track the last applied preferred_language in a ref
  // so we don't fire on every UI language change — that was Bug 1: clicking
  // the dropdown moved `language`, the effect fired, saw `language !==
  // user.preferred_language` (which hadn't been refetched yet), and snapped
  // the UI back to the cached preferred language.
  //
  // setLanguageLocal updates the UI without pushing back to the server,
  // avoiding an echo loop. The dropdown's `setLanguage` path takes care of
  // pushing to the server, and once user data refetches, `lastSyncedPrefRef`
  // catches up to the new value naturally.
  const lastSyncedPrefRef = useRef(null);
  useEffect(() => {
    if (!user || !user.preferred_language) {
      lastSyncedPrefRef.current = null;
      return;
    }
    if (!languages.length) return;  // wait for languages to load
    if (lastSyncedPrefRef.current === user.preferred_language) return;
    if (!languages.some(l => l.code === user.preferred_language)) return;
    lastSyncedPrefRef.current = user.preferred_language;
    setLanguageLocal(user.preferred_language);
  }, [user, languages, setLanguageLocal]);

  // 0.36.227 — Hydrate the global timestamp formatter at mount.
  // The system date/time format applies to every page regardless of
  // auth state (anonymous pages also show timestamps), so this lives
  // here at the App root, not behind a login gate. Failure is silent:
  // formatters fall back to their defaults ('dd.mm.yyyy', 'HH:mm').
  useEffect(() => {
    let cancelled = false;
    publicSettingsApi.get()
      .then(cfg => {
        if (cancelled) return;
        setSigninMode(cfg.signin_mode || '');
        setSystemTimeFormat({
          dateFormat: cfg.date_format,
          timeFormat: cfg.time_format,
          // 0.44.69 - The public site's pair.
          publicDateFormat: cfg.public_date_format,
          publicTimeFormat: cfg.public_time_format,
          defaultTimezone: cfg.default_timezone,
        });
        // 0.44.75 - System name and icon (tab title and icon).
        setSystemIdentity({ name: cfg.system_name, iconUrl: cfg.system_icon_url });
        // 0.46.77 - Buttons and focus.
        applyUiTheme(cfg.ui_theme, { store: true });
        // 0.44.89 - This installation's admin panel address.
        setAdminPanelUrl(cfg.admin_url);
        setPublicSiteUrl(cfg.public_url);
        // 0.36.278 — Dropdown thresholds piggyback on the same fetch.
        setDropdownThresholds({
          inlineThreshold: cfg.dropdown_inline_threshold,
          searchThreshold: cfg.dropdown_search_threshold,
        });
        // 0.38.16 - Platform-wide number-format defaults for the
        // expression cascade. Both blank by default; admin sets in
        // Platform Settings.
        setNumberFormatDefaults({
          defaultFormat: cfg.default_number_format || '',
          defaultLocale: cfg.default_number_locale || '',
          // 0.42.21 - Group only from this many digits; 0 = always.
          groupFromDigits: cfg.number_group_from_digits || 0,
        });
      })
      .catch(() => { /* defaults stay in place */ });
    return () => { cancelled = true; };
  }, []);

  // Admin routes do not depend on public i18n; render them even while public i18n is loading.
  if (isAdminRoute) {
    // 0.36.741 — Admin routes only render on the admin subdomain.
    // On the public hostname (caf.govtech.am), redirect to `/` so
    // /admin/* URLs don't expose any admin bundle — the landing page
    // is what they see instead. The redirect-not-404 keeps admins
    // who kept an old /admin bookmark from a dead page; they land
    // on the public site, click the admin panel link in their user
    // menu, and get sent cross-domain to the admin subdomain.
    if (!isAdminHost()) {
      return <Navigate to="/" replace />;
    }
    return (
      <RouteErrorBoundary><Suspense fallback={<div style={{padding:'2rem', textAlign:'center', color:'var(--color-text-500)'}}>Loading…</div>}><Routes>
        <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
          <Route index element={<RoleRoute roles={['admin']}><AdminOverview /></RoleRoute>} />
          <Route path="users" element={<RoleRoute roles={['admin']}><AdminUsers /></RoleRoute>} />
          <Route path="users/:publicId" element={<RoleRoute roles={['admin']}><AdminUserDetail /></RoleRoute>} />
          <Route path="helpdesk" element={<RoleRoute roles={['admin']}><AdminHelpdesk /></RoleRoute>} />
          {/* 0.36.273 — Languages moved from /admin/languages (sidebar
              entry) to /admin/settings/language (under the Settings
              card grid). Old URL redirects so bookmarks don't break. */}
          <Route path="languages" element={<Navigate to="/admin/settings/language" replace />} />
          <Route path="translations" element={<RoleRoute roles={['admin']}><AdminTranslations /></RoleRoute>} />
          <Route path="email-templates" element={<RoleRoute roles={['admin']}><AdminEmailTemplates /></RoleRoute>} />
          <Route path="email-templates/:slug" element={<RoleRoute roles={['admin']}><AdminEmailTemplateEditor /></RoleRoute>} />
          <Route path="pages" element={<RoleRoute roles={['admin']}><AdminStaticPages /></RoleRoute>} />
          {/* 0.36.292 — Platform layout config screens, reachable from the
              Header / Footer virtual rows on /admin/pages. */}
          {/* 0.44.06 - The site's navigation menu (first level). */}
          <Route path="layout/nav" element={<RoleRoute roles={['admin']}><AdminLayoutNav /></RoleRoute>} />
          <Route path="layout/header" element={<RoleRoute roles={['admin']}><AdminLayoutHeader /></RoleRoute>} />
          <Route path="layout/footer" element={<RoleRoute roles={['admin']}><AdminLayoutFooter /></RoleRoute>} />
          {/* 0.36.16 — Unified editor: ANY page (article/faq/form) opens
            * in the visual constructor at /admin/pages/:id. The legacy
            * /admin/forms/* routes redirect here for back-compat. */}
          <Route path="pages/:id" element={<RoleRoute roles={['admin']}><AdminFormEditor /></RoleRoute>} />
          <Route path="forms" element={<Navigate to="/admin/pages" replace />} />
          <Route path="forms/:id" element={<RoleRoute roles={['admin']}><AdminFormEditor /></RoleRoute>} />
          {/* 0.37.29 — Rule editor gets its own routed page instead
              of rendering inline within the Behavior tab. Two paths:
              /admin/forms/:formId/rules/new for adding, and
              /admin/forms/:formId/rules/:ruleIndex for editing the
              rule at that array index. See AdminRuleEditor.jsx. */}
          <Route path="forms/:formId/rules/new"
                 element={<RoleRoute roles={['admin']}><AdminRuleEditor /></RoleRoute>} />
          <Route path="forms/:formId/rules/:ruleIndex"
                 element={<RoleRoute roles={['admin']}><AdminRuleEditor /></RoleRoute>} />
          {/* /admin/forms/:id/preview is intentionally NOT defined here —
            * it falls through to the public Routes below so it can render
            * with the public Header + Footer (true citizen-experience
            * preview). Still admin-gated there. */}
          <Route path="audit-log" element={<RoleRoute roles={['admin', 'auditor']}><AdminAuditLog /></RoleRoute>} />
          {/* 0.36.345 — /admin/diagnostics route REMOVED. The Server
              resources + System log files panels now live inside the
              AdminOverview's System status card. */}
          {/* 0.36.381 — /admin/caf route removed with the CAF feature. */}
          <Route path="libraries" element={<RoleRoute roles={['admin']}><AdminLibrariesHome /></RoleRoute>} />
          {/* 0.36.351 — Generic library detail page with language tabs */}
          <Route path="libraries/view/:key" element={<RoleRoute roles={['admin']}><AdminLibraryDetail /></RoleRoute>} />
          {/* 0.36.354 — Dedicated references page (reached from the
              Usage column on the Libraries tab). */}
          <Route path="libraries/view/:key/references" element={<RoleRoute roles={['admin']}><AdminLibraryReferences /></RoleRoute>} />
          {/* 0.36.374 — The four dedicated ISO admin routes
              (libraries/iso-language-codes, iso-country-codes,
              iso-official-names, iso-dial-codes) are retired. Their
              inline CRUD is now on the unified detail page. The
              paths are preserved as redirects to the new
              /libraries/view/{key} location so external bookmarks
              still work. */}
          <Route path="libraries/iso-language-codes" element={<Navigate to="/admin/libraries/view/iso_language_codes" replace />} />
          <Route path="libraries/iso-country-codes" element={<Navigate to="/admin/libraries/view/iso_country_codes" replace />} />
          <Route path="libraries/iso-official-names" element={<Navigate to="/admin/libraries/view/iso_official_names" replace />} />
          <Route path="settings" element={<RoleRoute roles={['admin']}><AdminSettingsHome /></RoleRoute>} />
          <Route path="settings/email" element={<RoleRoute roles={['admin']}><AdminSettings /></RoleRoute>} />
          {/* 0.41.06 - Registered outbound API connections. */}
          {/* 0.43.50 - API connections moved to the sidebar, under
              Libraries; the old Settings address forwards there. */}
          <Route path="api-connections" element={<RoleRoute roles={['admin']}><AdminApiConnections key="general" kind="general" /></RoleRoute>} />
          <Route path="settings/api-connections" element={<Navigate to="/admin/api-connections" replace />} />
          {/* 0.43.40 - AI connectors: the same page, filtered to kind 'ai'.
              The key makes moving between the two cards load afresh. */}
          {/* 0.43.75 - AI prompts moved to the sidebar; the old Settings
              address forwards there. */}
          <Route path="ai-prompts" element={<RoleRoute roles={['admin']}><AdminApiConnections key="ai" kind="ai" /></RoleRoute>} />
          <Route path="settings/ai" element={<Navigate to="/admin/ai-prompts" replace />} />
          {/* 0.42.87 - Flows live beside Pages and forms now, not under
              Settings. The old address still answers, so a bookmark
              made while it was there keeps working. */}
          <Route path="flows" element={<RoleRoute roles={['admin']}><AdminFlows /></RoleRoute>} />
          {/* 0.45.45 - Collections are Libraries > Content libraries; the
              editor is a page of its own. */}
          <Route path="collections" element={<CollectionsRedirect />} />
          <Route path="libraries/content/:id" element={<RoleRoute roles={['admin']}><AdminCollections /></RoleRoute>} />
          <Route path="data-tables" element={<RoleRoute roles={['admin']}><AdminDataTables /></RoleRoute>} />
          <Route path="data-tables/:key" element={<RoleRoute roles={['admin']}><AdminDataTable /></RoleRoute>} />
          <Route path="content" element={<RoleRoute roles={['admin']}><AdminSiteContent /></RoleRoute>} />
          <Route path="content/:key" element={<RoleRoute roles={['admin']}><AdminContentItems /></RoleRoute>} />
          <Route path="content/:key/:itemId" element={<RoleRoute roles={['admin']}><AdminContentItem /></RoleRoute>} />
          <Route path="settings/flows" element={<Navigate to="/admin/flows" replace />} />
          <Route path="settings/branding" element={<RoleRoute roles={['admin']}><AdminBranding /></RoleRoute>} />
          <Route path="settings/system-identity" element={<RoleRoute roles={['admin']}><AdminSystemIdentity /></RoleRoute>} />
          <Route path="settings/ip-access" element={<RoleRoute roles={['admin']}><AdminIpAccess /></RoleRoute>} />
          <Route path="settings/addresses" element={<RoleRoute roles={['admin']}><AdminAddresses /></RoleRoute>} />
          <Route path="settings/geoip" element={<RoleRoute roles={['admin']}><AdminGeoIP /></RoleRoute>} />
          <Route path="settings/2fa" element={<RoleRoute roles={['admin']}><AdminTwoFactor /></RoleRoute>} />
          <Route path="settings/id-card" element={<RoleRoute roles={['admin']}><AdminIdCard /></RoleRoute>} />
          <Route path="settings/signup" element={<RoleRoute roles={['admin']}><AdminSignupMode /></RoleRoute>} />
          {/* 0.36.225 — Time & date settings card route */}
          <Route path="settings/time-date" element={<RoleRoute roles={['admin']}><AdminTimeDate /></RoleRoute>} />
          {/* 0.38.16 - Number-format defaults page (platform baseline). */}
          <Route path="settings/number-format" element={<RoleRoute roles={['admin']}><AdminNumberFormat /></RoleRoute>} />
          <Route path="settings/buttons-focus" element={<RoleRoute roles={['admin']}><AdminButtonsFocus /></RoleRoute>} />
          {/* 0.36.278 — Dropdown behavior thresholds card route */}
          <Route path="settings/dropdown-behavior" element={<RoleRoute roles={['admin']}><AdminDropdownBehavior /></RoleRoute>} />
          <Route path="settings/icons" element={<RoleRoute roles={['admin']}><AdminIcons /></RoleRoute>} />
          {/* 0.36.273 — System language settings (moved from the
              sidebar top-level Languages page). */}
          <Route path="settings/language" element={<RoleRoute roles={['admin']}><AdminLanguages /></RoleRoute>} />
          {/* 0.36.374 — Two-hop redirect for very-old bookmarks:
              /admin/settings/iso-* → /admin/libraries/view/{key}
              (the intermediate /admin/libraries/iso-* redirect is
              still there — same destination — but this way single-
              hop is enough). */}
          <Route path="settings/iso-language-codes" element={<Navigate to="/admin/libraries/view/iso_language_codes" replace />} />
          <Route path="settings/iso-country-codes" element={<Navigate to="/admin/libraries/view/iso_country_codes" replace />} />
          <Route path="settings/iso-official-names" element={<Navigate to="/admin/libraries/view/iso_official_names" replace />} />
          {/* 0.36.111 — In-product documentation.
            * 0.36.112 — /admin/docs is now a real index page (not a
            * redirect), since the forms guide joins the rules guide. */}
          <Route path="docs" element={<RoleRoute roles={['admin']}><AdminDocsIndex /></RoleRoute>} />
          <Route path="docs/rules" element={<RoleRoute roles={['admin']}><AdminDocsRules /></RoleRoute>} />
          <Route path="docs/forms" element={<RoleRoute roles={['admin']}><AdminDocsForms /></RoleRoute>} />
          <Route path="docs/libraries" element={<RoleRoute roles={['admin']}><AdminDocsLibraries /></RoleRoute>} />
          {/* 0.36.505 — Four additional guides. */}
          <Route path="docs/dropdowns" element={<RoleRoute roles={['admin']}><AdminDocsDropdowns /></RoleRoute>} />
          <Route path="docs/system-settings" element={<RoleRoute roles={['admin']}><AdminDocsSystemSettings /></RoleRoute>} />
          <Route path="docs/data-tables" element={<RoleRoute roles={['admin']}><AdminDocsDataTables /></RoleRoute>} />
          <Route path="docs/api-connections" element={<RoleRoute roles={['admin']}><AdminDocsApiConnections /></RoleRoute>} />
          <Route path="docs/ai-connectors" element={<RoleRoute roles={['admin']}><AdminDocsAiConnectors /></RoleRoute>} />
          <Route path="docs/library-management" element={<RoleRoute roles={['admin']}><AdminDocsLibraryManagement /></RoleRoute>} />
          <Route path="docs/library-references" element={<RoleRoute roles={['admin']}><AdminDocsLibraryReferences /></RoleRoute>} />
        </Route>
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes></Suspense></RouteErrorBoundary>
    );
  }

  if (loading) {
    return (
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: '60vh', color: 'var(--color-text-600)', fontSize: '14px',
      }}>
        Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: '60vh', padding: '40px', color: 'var(--color-error)', textAlign: 'center',
      }}>
        <div>
          <strong>Could not load the platform.</strong>
          <div style={{ marginTop: 8, color: 'var(--color-text-700)' }}>
            Backend may not be running on http://localhost:8000.
          </div>
          <div style={{ marginTop: 8, fontSize: 13, color: 'var(--color-text-600)' }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <PublicShell>
      {/* 0.36.733 — Admin subdomain branch. On gt18-admin.govtech.am
        * the SPA exposes only the login flow + a `/` that redirects
        * to /admin (which itself falls back to /login when not
        * authed). Everything else 404s to /login so citizen routes
        * aren't reachable on this hostname.
        *
        * Not a security boundary — admin routes are protected by
        * AdminRoute + RoleRoute wrappers that check the server
        * session. This is a UX affordance (dedicated URL for
        * bookmarks / muscle memory).
        *
        * Session isolation is automatic: cookies default to exact
        * hostname (no Domain=.govtech.am set on the session cookie),
        * so logging in on gt18-admin.* does NOT log you in on
        * caf.* and vice versa. */}
      {isAdminHost() ? (
        <RouteErrorBoundary><Suspense fallback={<div style={{padding:'2rem', textAlign:'center', color:'var(--color-text-500)'}}>Loading…</div>}><Routes>
          <Route path="/" element={<Navigate to="/admin" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/login/verify" element={<LoginVerify />} />
          <Route path="/login/id-card" element={<LoginIdCard />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          {/* 0.46.67 - The invite link points to the admin site (the
              admin sends it from there), which had no accept page: it
              fell to the catch-all below and showed the login, so an
              invited user could never set a password. After accepting,
              the page goes to /, which here is the admin panel. */}
          <Route path="/accept-invite/:token" element={<AcceptInvite />} />
          {/* 0.37.44 — Form preview lives on both hostnames. Was
              only registered in the public-hostname branch below,
              so admins on gt18-admin.govtech.am clicking Preview
              in the form editor hit the catch-all "* → /login"
              redirect and got kicked to the admin login. Same
              admin auth gates apply. Rendered inside PublicShell
              (this whole return is wrapped), and the preview URL
              is excluded from FORM_PREVIEW_RX / isAdminRoute so
              it gets citizen chrome — matches what a citizen
              would see. */}
          <Route
            path="/admin/forms/:id/preview"
            element={
              <AdminRoute>
                <RoleRoute roles={['admin']}>
                  <AdminFormPreview />
                </RoleRoute>
              </AdminRoute>
            }
          />
          {/* Everything else on the admin hostname → login. */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes></Suspense></RouteErrorBoundary>
      ) : (
      <RouteErrorBoundary><Suspense fallback={<div style={{padding:'2rem', textAlign:'center', color:'var(--color-text-500)'}}>Loading…</div>}><Routes>
        <Route path="/" element={<Landing />} />
        {/* 0.37.62 — Public /p/ routes now carry a language
            segment: /:lang/p/:slug (canonical). Old /p/:slug
            hits are redirected to /:currentLang/p/:slug so
            existing external links keep working; internal
            navigation goes straight to the lang-prefixed form.
            /about and /faq keep redirecting to their p/ slugs;
            the lang prefix is added by the redirect there. */}
        <Route path="/about" element={<RedirectToLangShortcut slug="about" />} />
        <Route path="/faq"   element={<RedirectToLangShortcut slug="faq" />} />
        <Route path="/:lang/p/:slug" element={<LangStaticPageRoute />} />
        {/* 0.42.63 - A flow answers in the SAME address space as pages,
            under the prefix it chooses, with the step as a further
            segment: /hy/services/vehicle-registration/2. A citizen should
            not be told from the URL whether a service is one page or
            five, which is what the short-lived /f/ prefix did. Slugs are
            unique across pages and flows, so an address belongs to one of
            them and the route below cannot be ambiguous. */}
        {/* 0.42.83 - A flow answers under /services/ only. */}
        <Route path="/:lang/services/:slug/:step" element={<KeyedFlowRunner />} />
        {/* 0.39.22 - Alternative "services" URL prefix. Renders the
            same StaticPage as /p/; a form's Settings picks which
            prefix its canonical URL uses. */}
        {/* 0.42.84 - /services/ belongs to flows entirely now that pages
            are /p/ only. Without this the bare address reached the PAGE
            component, whose flow fallback redirected to the same URL:
            a loop. */}
        <Route path="/:lang/services/:slug" element={<KeyedFlowRunner />} />
        <Route path="/p/:slug"       element={<RedirectToLangStaticPage />} />
        {/* 0.49.60 - A flow's address without the language, as a page's:
            /services/apply?training=t15 opens /<language>/services/apply
            with its values (it went to the home page). */}
        <Route path="/services/:slug" element={<RedirectToLangStaticPage />} />
        {/* 0.36.742 — Login family routes on the public hostname are
          * gated by the header's show_login_button config. When admin
          * turned the citizen login button off, typing /login (or any
          * of its siblings) in the URL bar redirects to `/` — the
          * citizen site is meant to be fully public in that mode.
          * Signup / forgot-password / accept-invite are all part of
          * the login/onboarding flow and also lose their public
          * entry points; they only make sense when login itself is
          * available. Admins log in via gt18-admin.govtech.am
          * (unaffected — that branch is above in the isAdminHost()
          * clause and doesn't consult this flag). */}
        {/* 0.46.82 - The public login page is designed in Pages and forms. */}
        <Route path="/login"           element={loginHiddenOnPublic ? <Navigate to="/" replace /> : <LoginPage />} />
        {/* 0.47.98 - And the rest of the sign-in pages (Pages and forms, tab Login). */}
        <Route path="/login/verify"    element={loginHiddenOnPublic ? <Navigate to="/" replace /> : <LoginVerifyPage />} />
        <Route path="/login/id-card"   element={loginHiddenOnPublic ? <Navigate to="/" replace /> : <LoginIdCardPage />} />
        <Route path="/signup"          element={loginHiddenOnPublic ? <Navigate to="/" replace /> : <SignupPage />} />
        <Route path="/forgot-password" element={loginHiddenOnPublic ? <Navigate to="/" replace /> : <ForgotPasswordPage />} />
        <Route path="/accept-invite/:token" element={loginHiddenOnPublic ? <Navigate to="/" replace /> : <AcceptInvitePage />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        {/* 0.36.381 — /caf/register route removed with the CAF feature. */}
        {/* Form preview — admin-gated but rendered with public chrome
          * (PublicShell wraps the whole block) so the preview faithfully
          * simulates the citizen experience: real Header at top, real
          * Footer at bottom, and full-bleed form elements span the true
          * viewport width. See FORM_PREVIEW_RX above for the exclusion
          * from the admin-shell branch. */}
        <Route
          path="/admin/forms/:id/preview"
          element={
            <AdminRoute>
              <RoleRoute roles={['admin']}>
                <AdminFormPreview />
              </RoleRoute>
            </AdminRoute>
          }
        />
        {/* 0.39.22 - Bare single-segment alias at the site root:
            caf.govtech.am/<uuid> -> resolve -> redirect to the
            canonical /<lang>/<prefix>/<slug>. AliasResolver only acts
            on UUID-shaped segments; anything else falls through to
            home, so this never shadows the named routes above. Placed
            last, just before the catch-all. */}
        <Route path="/:alias" element={<AliasResolver />} />
        {/* 0.39.25 - Language-prefixed alias for "no language"
            aliases: /<lang>/<uuid> -> resolve with that language ->
            /<lang>/<prefix>/<slug>. Only fires for UUID-shaped second
            segments (AliasResolver falls through otherwise), so it
            doesn't shadow /:lang/p or /:lang/services (3 segments) or
            other 2-segment routes. */}
        <Route path="/:lang/:alias" element={<AliasResolver />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes></Suspense></RouteErrorBoundary>
      )}
    </PublicShell>
  );
}

/**
 * 0.37.62 — Tiny helper: /about and /faq are shortcut URLs that
 * used to redirect straight to /p/about and /p/faq. Now the
 * target must include a language segment, and that segment is
 * only known at runtime (from LanguageContext / localStorage /
 * server preference). This component reads it and issues the
 * redirect. `replace` so the shortcut doesn't sit in history.
 */
function RedirectToLangShortcut({ slug }) {
  const { language } = useLanguage();
  /* 0.42.72 - A shortcut names no prefix, and guessing /p/ now sends a
     services page to a dead address. Hand it to the resolver, which
     asks the backend for the page's real one. */
  return <Navigate to={`/${language}/${slug}`} replace />;
}

/* 0.49.25 - A fresh flow for each slug (its steps keep one instance). */
function KeyedFlowRunner() {
  const { slug } = useParams();
  return <FlowRunner key={slug} />;
}

export default function App() {
  return (
    // 0.44.81 - IP access: a blocked address sees only "404 Not Found".
    <AccessGate>
    <LanguageProvider>
      <AuthProvider>
        <LayoutConfigProvider>
          {/* 0.47.96 - Under another site's path, routes start after it. */}
          <BrowserRouter basename={BASE || undefined}>
            {/* 0.49.25 - Links to other pages without reloading the site. */}
            <SpaLinks />
            <AppRouter />
          </BrowserRouter>
        </LayoutConfigProvider>
      </AuthProvider>
    </LanguageProvider>
    </AccessGate>
  );
}
